import { EmptyState, Loading } from "../../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router";
import {
  formatEuro,
  formatIntervalOf,
  LIBRARY_UNIT_LABELS,
  nlsfb,
  NLSFB_SEP,
  nlsfbTrail,
  OBLIGATION_BASES,
  OBLIGATION_BASIS_LABELS,
  OBLIGATION_KIND_TEXT,
  OBLIGATION_KINDS,
  OBLIGATION_STATUS_LABELS,
  PART_FILTER_LABELS,
  PART_FILTERS,
  PLAN_TYPE_LABELS,
  quantityText,
  WORK_TYPE_LABELS,
  WORK_TYPES,
  todayIso,
  type AssetObligationDto,
  type AssetObligationsResponse,
  type AssetReplacementDto,
  type LibraryUnit,
  type MjopPlanDto,
  type ObligationKind,
  type PartFilter,
  type PlanAssetDto,
  type PlanGroupDto,
  type PlanOccurrence,
  type PlanRowDto,
  type PlanType,
  type WorkType,
} from "@klantpunt/shared";
import {
  IconAssets,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconCopy,
  IconHistory,
  IconInfo,
  IconMjop,
  IconMore,
  IconPencil,
  IconPlus,
  IconTrash,
} from "../../../app/icons";
import { api, ApiError } from "../../../lib/api";
import { useToast } from "../../../lib/toast";
import { ExecutionPanel } from "../../obligations/ExecutionPanel";
import { AssetInfo } from "./AssetInfo";
import { BuildingHistory, HistorySection } from "./History";
import { euroInput } from "../MjopFields";
import {
  FIELD_LABELS,
  kEuro,
  ownQuantity,
  parseField,
  perTime,
  PLAN_WT,
  typeOptions,
  type Ctx,
  type Field,
  type PanelField,
  type Patch,
} from "./model";
import {
  amountIn,
  datedIn,
  fmtDay,
  periods,
  rangeLabel,
  shift,
  ZOOM_LABELS,
  ZOOMS,
  type Period,
  type Zoom,
} from "./periods";
import { Drawer, Popover, useMediaQuery } from "./ui";
import { Segmented, Tabs } from "../../../app/Choice";

/**
 * MJOP-planning per gebouw (docs/ASSETS-MJOP.md, "Planning"): per NL-SfB (3 posities) de assets met
 * hun uitvoeringen en posten. Twee weergaven: *Planning* (jaren, of ingezoomd op kwartaal, maand of
 * week) en *Gegevens* (werkblad). In de bewerkmodus wijzig je één cel tegelijk; details staan in een
 * paneel. Bedragen per keer op prijspeil, exclusief btw.
 */
export const planKey = (buildingId: string) => ["mjop", "plan", buildingId] as const;
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
/** Woorden van een uitvoering (keuring, onderhoud …); vervanging en herstel vallen terug op *Uitvoering*. */
const kindText = (t: PlanType) =>
  OBLIGATION_KIND_TEXT[(OBLIGATION_KINDS as readonly string[]).includes(t) ? (t as ObligationKind) : "overig"];
const recordLabel = (t: PlanType) => kindText(t).record;

type View = "plan" | "data";
type Attn = "overdue" | "soon" | "nocost" | "empty";
type Pop =
  | { kind: "row"; el: HTMLElement; x: Ctx; sure?: boolean }
  | { kind: "occ"; el: HTMLElement; x: Ctx; year: number; occ: PlanOccurrence[] }
  | { kind: "asset"; el: HTMLElement; a: PlanAssetDto; g: PlanGroupDto };

const DATA_COLS: { key: Field | "per" | "status"; label: string; num?: boolean }[] = [
  { key: "type", label: "Type" },
  { key: "basis", label: "Eis" },
  { key: "start", label: "Start" },
  { key: "end", label: "Stopjaar" },
  { key: "interval", label: "Interval" },
  { key: "cost", label: "Per keer", num: true },
  { key: "per", label: "Gem. per jaar", num: true },
  { key: "status", label: "Status" },
];
const isField = (k: string): k is Field => ["type", "basis", "start", "end", "interval", "cost"].includes(k);

const intervalText = (r: { intervalMonths: number | null; intervalWeeks: number | null }) =>
  formatIntervalOf(r.intervalMonths, r.intervalWeeks);
const everyText = (r: { intervalMonths: number | null; intervalWeeks: number | null }) =>
  r.intervalWeeks === 1
    ? "elke week"
    : r.intervalWeeks
      ? `elke ${r.intervalWeeks} weken`
      : r.intervalMonths
        ? `elke ${intervalText(r)}`
        : "eenmalig";
const startText = (r: PlanRowDto) => (r.source === "obligation" ? fmtDay(r.start) : (r.start?.slice(0, 4) ?? "—"));
const rawValue = (r: PlanRowDto, key: Field): string =>
  ({
    type: r.type,
    basis: r.basis ?? "",
    start: r.source === "obligation" ? (r.start ?? "") : (r.start?.slice(0, 4) ?? ""),
    end: r.endYear?.toString() ?? "",
    interval: intervalText(r),
    cost: euroInput(r.costCents),
  })[key];

/**
 * Naam van een groep met de bovenliggende niveaus gedempt ervoor: "58.2 Meet- en regelinstallaties ›
 * centraal". Een groep op elementniveau krijgt de hoofdgroep ervoor. Het laatste niveau blijft heel;
 * het pad ervoor kapt af als de kolom te smal is. Het volledige pad staat in de tooltip.
 */
function GroupName({ code, label }: { code: string | null; label: string }) {
  const main = nlsfb(code).main;
  const trail = nlsfbTrail(code);
  if (!main || !trail.length) return <span className="pl-gname">{label}</span>;
  const up = trail.length > 1 ? trail.slice(0, -1) : [main.label];
  return (
    <span className="pl-gname" title={[main.label, ...trail].join(NLSFB_SEP)}>
      <span className="pl-gup">{`${up.join(NLSFB_SEP)}${NLSFB_SEP}`}</span>
      <span className="pl-glast">{trail[trail.length - 1]}</span>
    </span>
  );
}

export function PlanTable({
  buildingId,
  canEditItems,
  canEditObligations,
  onDetails,
}: {
  buildingId: string;
  /** MJOP bewerken: posten, losse regels, jaren verschuiven. */
  canEditItems: boolean;
  /** Assets beheren: uitvoeringen bij een asset. */
  canEditObligations: boolean;
  /** Post: alle velden (hoeveelheid, prijspeil, status). */
  onDetails: (itemId: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const phone = useMediaQuery("(max-width: 760px)");
  const q = useQuery({
    queryKey: planKey(buildingId),
    queryFn: () => api<MjopPlanDto>(`/buildings/${buildingId}/mjop-plan`),
  });
  const plan = q.data;
  const today = plan?.today ?? todayIso();
  const [view, setView] = useState<View>("plan");
  const [zoom, setZoom] = useState<Zoom>("year");
  const [anchor, setAnchor] = useState(today);
  const [edit, setEdit] = useState(false);
  const [search, setSearch] = useState("");
  const [wts, setWts] = useState<Set<WorkType>>(new Set(WORK_TYPES));
  const [partFilter, setPartFilter] = useState<PartFilter>("all");
  const [attn, setAttn] = useState<Attn | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [drawer, setDrawer] = useState<{ id: string; focusName?: boolean } | null>(null);
  /** Uitvoering bij een asset: hetzelfde formulier als in het assetpaneel (null = nieuw). */
  // a null = onderhoud of keuring bij het gebouw zelf.
  const [exec, setExec] = useState<{ a: PlanAssetDto | null; id: string | null } | null>(null);
  /** ⓘ bij een asset: foto's, plek op de tekening en gegevens. */
  const [info, setInfo] = useState<{ a: PlanAssetDto; g: PlanGroupDto } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState<{ id: string; key: Field; initial?: string } | null>(null);
  const [cellError, setCellError] = useState<{ id: string; key: Field; msg: string } | null>(null);
  const [pop, setPop] = useState<Pop | null>(null);
  const [pending, setPending] = useState(0);
  const [saved, setSaved] = useState(false);
  const focusReq = useRef<{ id: string; key: Field } | null>(null);
  const gridRef = useRef<HTMLTableElement>(null);
  const canEdit = canEditItems || canEditObligations;
  const closePop = useCallback(() => setPop(null), []);

  const cols = useMemo(
    () => (plan ? periods(zoom, anchor, plan.fromYear, plan.toYear, today) : []),
    [plan, zoom, anchor, today],
  );

  // ---------- opslaan ----------
  const refresh = () => {
    for (const key of ["mjop", "budget-items", "obligation-list", "obligations", "asset-replacement"])
      void qc.invalidateQueries({ queryKey: [key] });
  };
  /** Voert een wijziging uit met de opslagstatus; geeft de fout terug (of null). */
  async function run(fn: () => Promise<unknown>, done?: string): Promise<string | null> {
    setPending((n) => n + 1);
    try {
      await fn();
      refresh();
      setSaved(true);
      if (done) toast(done);
      return null;
    } catch (e) {
      const m = errMsg(e);
      toast(m);
      return m;
    } finally {
      setPending((n) => n - 1);
    }
  }
  const patchRow = (r: PlanRowDto, patch: Patch) =>
    run(() =>
      api(r.source === "obligation" ? `/asset-obligations/${r.id}` : `/budget-items/${r.id}`, {
        method: "PATCH",
        body: patch,
      }),
    );
  const canEditRow = (r: PlanRowDto) => (r.source === "obligation" ? canEditObligations : canEditItems);

  /**
   * Vervanging van de asset uit de waarden van een uitvoering (interval → levensduur, per keer → prijs,
   * volgende uitvoering → startjaar). Met `obligationId` wordt die uitvoering daarna verwijderd (omzetten).
   */
  async function putReplacement(
    a: PlanAssetDto,
    o: Pick<AssetObligationDto, "intervalMonths" | "costCents" | "nextDueOn">,
    obligationId?: string,
  ) {
    const cur = await api<AssetReplacementDto>(`/assets/${a.id}/replacement`);
    const years = o.intervalMonths && o.intervalMonths % 12 === 0 ? o.intervalMonths / 12 : null;
    const after = await api<AssetReplacementDto>(`/assets/${a.id}/replacement`, {
      method: "PUT",
      body: {
        lifespanYears: years ?? cur.lifespanYears ?? cur.suggestion?.lifespanYears ?? 25,
        unitPriceCents: o.costCents ?? cur.unitPriceCents ?? cur.suggestion?.unitPriceCents ?? 0,
        quantity: cur.quantity,
        unit: cur.unit,
        priceLevelYear: cur.priceLevelYear,
      },
    });
    if (after.item && o.nextDueOn)
      await api(`/budget-items/${after.item.id}`, {
        method: "PATCH",
        body: { firstYear: Number(o.nextDueOn.slice(0, 4)) },
      });
    if (obligationId) await api(`/asset-obligations/${obligationId}`, { method: "DELETE" });
  }
  /** Bestaande uitvoering omzetten naar de vervanging van de asset. */
  const toReplacement = (x: Ctx) =>
    run(async () => {
      const a = x.a!;
      const obs = await api<AssetObligationsResponse>(`/assets/${a.id}/obligations`);
      const o = obs.obligations.find((v) => v.id === x.r.id);
      if (!o) throw new Error("Regel bestaat niet meer");
      await putReplacement(a, o, o.id);
    }, "Omgezet naar vervanging van de asset");

  /** Wijziging uit een cel of het paneel; geeft een foutmelding terug of null. */
  async function saveField(x: Ctx, key: PanelField, value: string): Promise<string | null> {
    const p = parseField(x.r, key, value);
    if ("error" in p) {
      toast(p.error);
      return p.error;
    }
    if ("replace" in p) return toReplacement(x);
    return patchRow(x.r, p.patch);
  }

  const addOwn = (a: PlanAssetDto) => setExec({ a, id: null });
  /** Uitvoering bij een asset: het gedeelde formulier; posten: het paneel van de MJOP. */
  const openRow = (x: Ctx) =>
    x.r.source === "obligation" ? setExec({ a: x.a, id: x.r.id }) : setDrawer({ id: x.r.id });
  /** Onderhoud en keuringen van een andere asset overnemen (de vervanging hoort bij de asset zelf). */
  const copyFrom = (a: PlanAssetDto, src: PlanAssetDto) =>
    run(async () => {
      const { obligations } = await api<AssetObligationsResponse>(`/assets/${src.id}/obligations`);
      for (const o of obligations)
        await api(`/assets/${a.id}/obligations`, {
          method: "POST",
          body: {
            id: crypto.randomUUID(),
            name: o.name,
            kind: o.kind,
            intervalMonths: o.intervalMonths,
            intervalWeeks: o.intervalWeeks,
            costCents: o.costCents,
            priceLevelYear: o.priceLevelYear,
            basis: o.basis,
            performer: o.performer,
            appliesWhen: o.appliesWhen,
            sourceTitle: o.sourceTitle,
            sourceUrl: o.sourceUrl,
            notApplicable: o.notApplicable,
            overrideReason: o.overrideReason,
            endYear: o.endYear,
          },
        });
    }, `Gekopieerd van ${src.code} ${src.name}`);
  const addLoose = (elementCode: string | null) => {
    const id = crypto.randomUUID();
    void run(() =>
      api(`/buildings/${buildingId}/budget-items`, {
        method: "POST",
        body: {
          id,
          workType: "maintenance",
          description: "Nieuwe regel",
          unitPriceCents: 0,
          firstYear: Number(today.slice(0, 4)) + 1,
          cycleYears: 1,
          elementCode,
        },
      }),
    ).then((err) => !err && setDrawer({ id, focusName: true }));
  };
  const remove = (x: Ctx) =>
    run(
      () =>
        api(x.r.source === "obligation" ? `/asset-obligations/${x.r.id}` : `/budget-items/${x.r.id}`, {
          method: "DELETE",
        }),
      `"${x.r.description}" verwijderd`,
    ).then(() => drawer?.id === x.r.id && setDrawer(null));
  const duplicate = (x: Ctx) =>
    run(async () => {
      const { obligations } = await api<AssetObligationsResponse>(`/assets/${x.a!.id}/obligations`);
      const o = obligations.find((v) => v.id === x.r.id);
      if (!o) return;
      await api(`/assets/${x.a!.id}/obligations`, {
        method: "POST",
        body: {
          id: crypto.randomUUID(),
          name: `${o.name} (kopie)`,
          kind: o.kind,
          intervalMonths: o.intervalMonths,
          intervalWeeks: o.intervalWeeks,
          costCents: o.costCents,
          basis: o.basis,
          nextDueOn: o.nextDueOn,
          endYear: o.endYear,
        },
      });
    }, "Regel gedupliceerd");
  /** Eén uitvoermoment van een post: verschuiven, overslaan, ander bedrag of herstellen. */
  const override = (r: PlanRowDto, orig: number, body: Record<string, unknown> | null) =>
    run(() => api(`/budget-items/${r.id}/overrides/${orig}`, body ? { method: "PUT", body } : { method: "DELETE" }));

  // ---------- filteren ----------
  const all = useMemo<Ctx[]>(
    () =>
      (plan?.groups ?? []).flatMap((g) => [
        ...g.assets.flatMap((a) => a.rows.map((r) => ({ r, a, g }))),
        ...g.loose.map((r) => ({ r, a: null, g })),
      ]),
    [plan],
  );
  const counts = useMemo(
    () => ({
      overdue: all.filter((x) => x.r.status === "overdue").length,
      soon: all.filter((x) => x.r.status === "soon").length,
      nocost: all.filter((x) => perTime(x.r) == null && !x.r.notApplicable).length,
      // Een onderdeel zonder regels valt onder zijn hoofdasset en telt niet als "zonder uitvoeringen".
      empty: (plan?.groups ?? []).flatMap((g) => g.assets).filter((a) => !a.rows.length && !a.parentAssetId).length,
    }),
    [all, plan],
  );
  const needle = search.trim().toLowerCase();
  const visible = (x: Ctx) => {
    if (!wts.has(PLAN_WT[x.r.type])) return false;
    if (needle && !`${x.r.description} ${x.a?.name ?? ""} ${x.a?.code ?? ""}`.toLowerCase().includes(needle))
      return false;
    if (attn === "overdue") return x.r.status === "overdue";
    if (attn === "soon") return x.r.status === "soon";
    if (attn === "nocost") return perTime(x.r) == null && !x.r.notApplicable;
    if (attn === "empty") return false;
    return true;
  };
  const filtering = Boolean(needle || attn || wts.size < WORK_TYPES.length);
  const byId = (id: string) => all.find((x) => x.r.id === id) ?? null;

  // ---------- werkblad: focus en toetsenbord ----------
  useEffect(() => {
    const f = focusReq.current;
    if (!f || editing) return;
    const td = gridRef.current?.querySelector<HTMLElement>(`td[data-id="${f.id}"][data-key="${f.key}"]`);
    if (td) {
      td.focus();
      focusReq.current = null;
    }
  });
  const cellAt = (r: number, c: number) =>
    gridRef.current?.querySelector<HTMLElement>(`td[data-r="${r}"][data-c="${c}"]`);
  function onCellKey(e: KeyboardEvent<HTMLTableCellElement>, x: Ctx, key: Field) {
    const td = e.currentTarget;
    const r = Number(td.dataset.r);
    const c = Number(td.dataset.c);
    const mv = ({ ArrowDown: [1, 0], ArrowUp: [-1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] } as const)[
      e.key as "ArrowDown"
    ];
    if (mv) {
      e.preventDefault();
      let n = cellAt(r + mv[0], c + mv[1]);
      for (let s = c; !n && mv[0] && s >= 0; s--) n = cellAt(r + mv[0], s);
      n?.focus();
    } else if (e.key === "Enter" || e.key === "F2") {
      e.preventDefault();
      setEditing({ id: x.r.id, key });
    } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && key !== "type" && key !== "basis") {
      e.preventDefault();
      setEditing({ id: x.r.id, key, initial: e.key });
    }
  }
  async function commitCell(x: Ctx, key: Field, value: string, move: [number, number] | null) {
    const td = gridRef.current?.querySelector<HTMLElement>(`td[data-id="${x.r.id}"][data-key="${key}"]`);
    const r = Number(td?.dataset.r);
    const c = Number(td?.dataset.c);
    setEditing(null);
    focusReq.current = { id: x.r.id, key };
    if (value === rawValue(x.r, key)) {
      if (move) cellAt(r + move[0], c + move[1])?.focus();
      return;
    }
    const err = await saveField(x, key, value);
    setCellError(err ? { id: x.r.id, key, msg: err } : null);
    if (!err && move) {
      const n = cellAt(r + move[0], c + move[1]);
      if (n) focusReq.current = { id: n.dataset.id!, key: n.dataset.key as Field };
    }
  }

  // ---------- planning: cellen ----------
  function planCells(x: Ctx, occ: PlanOccurrence[]): ReactNode[] {
    const r = x.r;
    const wt = PLAN_WT[r.type];
    const out: ReactNode[] = [];
    const cls = (p: Period) => `pl-yr${p.now ? " now" : ""}${p.yearStart ? " ys" : ""}`;
    const chip = (
      key: string,
      className: string,
      label: string,
      body: ReactNode,
      year: number,
      list: PlanOccurrence[],
    ) => (
      <button
        type="button"
        className={`pl-occ wt-${wt} ${className}`}
        aria-label={label}
        title={label}
        onClick={(e) => (edit ? setPop({ kind: "occ", el: e.currentTarget, x, year, occ: list }) : openRow(x))}
      >
        {body}
      </button>
    );
    if (zoom === "year" || r.source === "item") {
      // Jaarweergave, of een post (alleen een jaar): één blok over het hele jaar.
      for (let i = 0; i < cols.length;) {
        const p = cols[i]!;
        let span = 1;
        while (zoom !== "year" && i + span < cols.length && cols[i + span]!.year === p.year) span++;
        const list = occ.filter((o) => o.year === p.year);
        if (!list.length) {
          for (let j = 0; j < span; j++) out.push(<td key={cols[i + j]!.from} className={cls(cols[i + j]!)} />);
          i += span;
          continue;
        }
        const live = list.filter((o) => !o.skipped);
        const n = live.length;
        const amt = live.reduce((s, o) => s + o.amountCents, 0);
        const skipped = !n;
        const moved = list.some((o) => o.moved);
        const why = list.find((o) => o.reason)?.reason;
        const over = p.year === Number(today.slice(0, 4)) && r.status === "overdue";
        const band = zoom !== "year";
        const value = skipped ? "overgeslagen" : perTime(r) == null ? "?" : kEuro(amt);
        const label = skipped
          ? `${p.year} overgeslagen`
          : `${p.year}: ${formatEuro(amt)}${n > 1 ? `, ${n} keer` : ""}${moved ? ", verschoven" : ""}${band ? ", geen datum binnen het jaar" : ""}`;
        const full = why ? `${label}. Reden: ${why}` : label;
        const className = [skipped ? "skip" : "", moved ? "moved" : "", over ? "over" : "", band ? "band" : ""].join(
          " ",
        );
        const body = band ? (
          <>
            <small>{p.year}, heel jaar</small>
            <span>{value}</span>
          </>
        ) : (
          <>
            {n > 1 && <small>{n}×</small>}
            {value}
          </>
        );
        out.push(
          <td key={p.from} className={cls(p)} colSpan={span > 1 ? span : undefined}>
            {chip(p.from, className, full, body, p.year, list)}
          </td>,
        );
        i += span;
      }
      return out;
    }
    for (const p of cols) {
      const list = datedIn(occ, p);
      if (!list.length) {
        out.push(<td key={p.from} className={cls(p)} />);
        continue;
      }
      const amt = list.reduce((s, o) => s + o.amountCents, 0);
      const over = list.some((o) => o.date === today) && r.status === "overdue";
      const label = `${list.map((o) => fmtDay(o.date)).join(", ")}${perTime(r) == null ? "" : `: ${formatEuro(amt)}`}${over ? ", verlopen" : ""}`;
      out.push(
        <td key={p.from} className={cls(p)}>
          {chip(
            p.from,
            over ? "over" : "",
            label,
            <>
              {list.length > 1 && <small>{list.length}×</small>}
              {perTime(r) == null ? "?" : kEuro(amt)}
            </>,
            p.year,
            list,
          )}
        </td>,
      );
    }
    return out;
  }
  const sumCells = (xs: Ctx[], className: string) =>
    cols.map((p) => {
      const v = xs.reduce((s, x) => s + amountIn(x.r.occurrences, p, zoom), 0);
      return (
        <td key={p.from} className={`${className} pl-yr${p.now ? " now" : ""}${p.yearStart ? " ys" : ""}`}>
          {v ? kEuro(v) : <span className="pl-zero">·</span>}
        </td>
      );
    });

  // ---------- werkblad: cellen ----------
  function dataCells(x: Ctx, ri: number): ReactNode[] {
    const r = x.r;
    const opts = typeOptions(x, canEditItems);
    return DATA_COLS.map((c, ci) => {
      const key = c.key;
      const editable = edit && canEditRow(r) && isField(key) && !(key === "type" && !opts);
      const err = cellError && cellError.id === r.id && cellError.key === key;
      const content = cellContent(x, key);
      if (!editable || !isField(key))
        return (
          <td key={key} className={c.num ? "r" : undefined}>
            <span className="pl-cell">{content}</span>
          </td>
        );
      const isEditing = editing?.id === r.id && editing.key === key;
      return (
        <td
          key={key}
          className={`pl-ed${c.num ? " r" : ""}${err ? " bad" : ""}`}
          tabIndex={-1}
          data-r={ri}
          data-c={ci}
          data-id={r.id}
          data-key={key}
          title={err ? cellError.msg : undefined}
          onClick={() => !isEditing && setEditing({ id: r.id, key })}
          onKeyDown={(e) => !isEditing && onCellKey(e, x, key)}
        >
          {isEditing ? (
            <CellEditor
              x={x}
              field={key}
              initial={editing.initial}
              options={key === "type" ? opts : null}
              onDone={(value, move) =>
                value === null
                  ? (setEditing(null), (focusReq.current = { id: r.id, key }))
                  : void commitCell(x, key, value, move)
              }
            />
          ) : (
            <span className="pl-cell">{content}</span>
          )}
        </td>
      );
    });
  }

  // ---------- render ----------
  if (q.isPending)
    return (
      <section className="panel" aria-label="Planning" aria-busy="true">
        <Loading />
      </section>
    );
  if (!plan) return null;
  const busy = pending > 0;
  const hasParts = plan.groups.some((g) => g.assets.some((a) => a.parentAssetId));
  // Filter Onderdelen: zonder onderdelen, of alleen de onderdelen (losse regels dan niet).
  const groups =
    partFilter === "all"
      ? plan.groups
      : plan.groups
          .map((g) => {
            const assets = g.assets.filter((a) => (partFilter === "parts") === Boolean(a.parentAssetId));
            const loose = partFilter === "parts" ? [] : g.loose;
            const sum = (rows: PlanRowDto[]) =>
              rows.reduce((t, r) => t + (r.notApplicable ? 0 : (r.perYearCents ?? 0)), 0);
            return {
              ...g,
              assets,
              loose,
              perYearCents: assets.reduce((t, a) => t + sum(a.rows), 0) + sum(loose),
            };
          })
          .filter((g) => g.assets.length || g.loose.length);
  const parentCode = (id: string) => plan.groups.flatMap((g) => g.assets).find((a) => a.id === id)?.code ?? "";
  const ncols = view === "plan" ? cols.length : DATA_COLS.length;
  const showCards = phone;
  let ri = 0;
  const rows: ReactNode[] = [];
  const rowFirst = (x: Ctx) => {
    const r = x.r;
    const meta =
      view === "plan"
        ? [
            PLAN_TYPE_LABELS[r.type],
            everyText(r),
            perTime(r) == null ? "geen bedrag" : `${formatEuro(perTime(r)!)} per keer`,
            r.basis ? OBLIGATION_BASIS_LABELS[r.basis] : null,
          ]
            .filter(Boolean)
            .join(" · ")
        : r.source === "obligation"
          ? "Uitvoering bij asset"
          : r.type === "vervanging"
            ? `Vervanging uit levensduur${ownQuantity(x) ? " · eigen aantal" : ""}`
            : r.type === "herstel"
              ? `Uit raming bij bevinding ${r.findingCode ?? ""}`
              : "Losse regel";
    return (
      <th scope="row" className="pl-first">
        <div className="pl-rowhead">
          <i className={`pl-tdot wt-${PLAN_WT[r.type]}`} aria-hidden="true" />
          <div className="pl-txt">
            <button type="button" className="pl-rname" onClick={() => openRow(x)}>
              {r.description}
            </button>
            <span className="pl-rmeta" title={meta}>
              {meta}
            </span>
          </div>
          {
            <button
              type="button"
              className="pl-ico pl-more"
              aria-label={`Acties voor ${r.description}`}
              aria-haspopup="menu"
              aria-expanded={pop?.kind === "row" && pop.x.r.id === r.id}
              onClick={(e) =>
                setPop(pop?.kind === "row" && pop.x.r.id === r.id ? null : { kind: "row", el: e.currentTarget, x })
              }
            >
              <IconMore size={18} aria-hidden="true" />
            </button>
          }
        </div>
      </th>
    );
  };
  const rowEl = (x: Ctx) => {
    const cls = `pl-row${drawer?.id === x.r.id || exec?.id === x.r.id ? " sel" : ""}${x.r.notApplicable ? " na" : ""}`;
    return (
      <tr key={x.r.id} className={cls} data-testid="plan-row">
        {rowFirst(x)}
        {view === "plan" ? planCells(x, x.r.occurrences) : dataCells(x, ri++)}
      </tr>
    );
  };
  const copySource = (a: PlanAssetDto, g: PlanGroupDto) =>
    g.assets.find((o) => o.id !== a.id && o.rows.some((r) => r.source === "obligation")) ?? null;

  for (const g of groups) {
    const gx = [
      ...g.assets.flatMap((a) => a.rows.map((r) => ({ r, a, g }))),
      ...g.loose.map((r) => ({ r, a: null, g })),
    ];
    const shown = gx.filter(visible);
    const emptyAssets = g.assets.filter((a) => !a.rows.length && !a.parentAssetId);
    if (attn === "empty" ? !emptyAssets.length : filtering && !shown.length) continue;
    const key = g.key ?? "none";
    const open = !collapsed.has(key);
    rows.push(
      <tr key={`g-${key}`} className="pl-grp">
        <th scope="row" className="pl-first">
          <button
            type="button"
            className="pl-gbtn"
            aria-expanded={open}
            onClick={() => {
              const n = new Set(collapsed);
              if (open) n.add(key);
              else n.delete(key);
              setCollapsed(n);
            }}
          >
            <IconChevronDown size={16} aria-hidden="true" />
            {g.key && <span className="pl-gcode">{g.key}</span>} <GroupName code={g.key} label={g.label} />
          </button>
        </th>
        {view === "plan" ? (
          sumCells(shown, "r pl-gsum")
        ) : (
          <>
            <td colSpan={ncols - 2} />
            <td className="r">
              {formatEuro(shown.reduce((s, x) => s + (x.r.notApplicable ? 0 : (x.r.perYearCents ?? 0)), 0))}
            </td>
            <td />
          </>
        )}
      </tr>,
    );
    if (!open) continue;
    for (const a of attn === "empty" ? emptyAssets : g.assets) {
      const ar = a.rows.map((r) => ({ r, a, g })).filter(visible);
      if (attn !== "empty" && filtering && !ar.length) continue;
      const n = a.rows.length;
      const src = copySource(a, g);
      rows.push(
        <tr key={`a-${a.id}`} className={`pl-asset${n ? "" : " empty"}${a.parentAssetId ? " pl-part" : ""}`}>
          <th scope="row" className="pl-first">
            <div className="pl-ahead">
              <span className="pl-aico" aria-hidden="true">
                <IconAssets size={15} />
              </span>
              <div className="pl-atxt">
                <span className="pl-aname">
                  <span className="pl-acode">{a.code}</span>
                  {a.name}
                </span>
                <span className="pl-asub">
                  {[
                    a.quantity != null ? `${a.quantity.toLocaleString("nl-NL")} ${a.unit ?? ""}`.trim() : null,
                    a.yearBuilt ? `bouwjaar ${a.yearBuilt}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  {a.quantity != null || a.yearBuilt ? " · " : ""}
                  {n ? (
                    `${n} ${n === 1 ? "regel" : "regels"}${a.parentAssetId ? ` · eigen post naast ${parentCode(a.parentAssetId)}` : ""}`
                  ) : a.parentAssetId ? (
                    `valt onder ${parentCode(a.parentAssetId)}`
                  ) : (
                    <span className="pl-anone">nog niets gepland</span>
                  )}
                </span>
              </div>
              <button
                type="button"
                className="pl-ico pl-ainfo"
                aria-label={`Informatie over ${a.code} ${a.name}`}
                onClick={() => setInfo({ a, g })}
              >
                <IconInfo size={16} aria-hidden="true" />
              </button>
              {/* Zonder regels staan dezelfde acties al als knoppen onder de asset (niet bij een onderdeel) */}
              {canEditObligations && (n > 0 || a.parentAssetId) && (
                <button
                  type="button"
                  className="pl-ico pl-aadd"
                  aria-label={`Toevoegen bij ${a.code} ${a.name}`}
                  aria-haspopup="menu"
                  aria-expanded={pop?.kind === "asset" && pop.a.id === a.id}
                  onClick={(e) => setPop({ kind: "asset", el: e.currentTarget, a, g })}
                >
                  <IconPlus size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          </th>
          {view === "plan" ? (
            sumCells(ar, "r pl-asum")
          ) : (
            <>
              <td colSpan={ncols - 2} />
              <td className="r pl-asum">
                {(() => {
                  const v = ar.reduce((s, x) => s + (x.r.notApplicable ? 0 : (x.r.perYearCents ?? 0)), 0);
                  return v ? formatEuro(v) : "";
                })()}
              </td>
              <td />
            </>
          )}
        </tr>,
      );
      for (const x of ar) rows.push(rowEl(x));
      if (!n && !a.parentAssetId) {
        if (canEditObligations)
          rows.push(
            <tr key={`s-${a.id}`} className="pl-add pl-sugg">
              <th className="pl-first">
                <div className="pl-sact">
                  <button type="button" className="btn small primary" onClick={() => addOwn(a)}>
                    <IconPlus size={14} aria-hidden="true" />
                    Uitvoering toevoegen
                  </button>
                  {src && (
                    <button type="button" className="btn small" onClick={() => void copyFrom(a, src)}>
                      Kopiëren van {src.code} {src.name}
                    </button>
                  )}
                </div>
              </th>
              <td colSpan={ncols} />
            </tr>,
          );
      } else if (edit && canEditObligations)
        rows.push(
          <tr key={`add-${a.id}`} className="pl-add">
            <th className="pl-first">
              <button type="button" className="pl-addbtn" onClick={() => addOwn(a)}>
                <IconPlus size={14} aria-hidden="true" />
                Uitvoering bij {a.code}
              </button>
            </th>
            <td colSpan={ncols} />
          </tr>,
        );
    }
    const loose = g.loose.map((r) => ({ r, a: null, g })).filter(visible);
    if (attn !== "empty" && loose.length)
      rows.push(
        <tr key={`lh-${key}`} className="pl-loosehead">
          <th scope="row" className="pl-first">
            Losse regels
          </th>
          <td colSpan={ncols} />
        </tr>,
        ...loose.map(rowEl),
      );
    if (attn !== "empty" && edit && canEditItems)
      rows.push(
        <tr key={`al-${key}`} className="pl-add">
          <th className="pl-first">
            <button type="button" className="pl-addbtn" onClick={() => addLoose(g.key)}>
              <IconPlus size={14} aria-hidden="true" />
              Losse regel{g.key ? ` in ${g.key}` : ""}
            </button>
          </th>
          <td colSpan={ncols} />
        </tr>,
      );
  }
  const shownAll = all.filter(visible);
  const drawerX = drawer ? byId(drawer.id) : null;

  return (
    <section className="panel pl" aria-label="Planning">
      <div className="titlerow">
        <h2>Planning</h2>
        <span className="btnrow">
          <button type="button" className="btn ghost" onClick={() => setShowHistory(true)}>
            <IconHistory size={20} aria-hidden="true" />
            Wijzigingen
          </button>
          {canEdit && (
            <>
              <span className={`pl-saved${busy ? " busy" : ""}`} aria-live="polite">
                {(busy || saved) && (
                  <>
                    <i aria-hidden="true" />
                    {busy ? "Opslaan…" : "Alles opgeslagen"}
                  </>
                )}
              </span>
              {(edit || phone) && canEditItems && (
                <button type="button" className="btn" onClick={() => addLoose(null)}>
                  <IconPlus size={20} aria-hidden="true" />
                  Losse regel
                </button>
              )}
              {!phone && (
                <button
                  type="button"
                  className="btn primary"
                  aria-pressed={edit}
                  onClick={() => {
                    setEdit(!edit);
                    setEditing(null);
                    setPop(null);
                  }}
                >
                  <IconPencil size={20} aria-hidden="true" />
                  {edit ? "Klaar" : "Bewerken"}
                </button>
              )}
            </>
          )}
        </span>
      </div>

      {(counts.overdue > 0 || counts.soon > 0 || counts.nocost > 0 || counts.empty > 0) && (
        <div className="pl-attn" role="group" aria-label="Aandacht">
          {(
            [
              ["overdue", counts.overdue, (n: number) => `${n} verlopen`],
              ["soon", counts.soon, (n: number) => `${n} binnenkort`],
              ["nocost", counts.nocost, (n: number) => `${n} zonder bedrag`],
              ["empty", counts.empty, (n: number) => `${n} ${n === 1 ? "asset" : "assets"} zonder uitvoeringen`],
            ] as const
          )
            .filter(([, n]) => n > 0)
            .map(([k, n, t]) => (
              <button
                key={k}
                type="button"
                className={`pl-achip a-${k}`}
                aria-pressed={attn === k}
                onClick={() => {
                  setAttn(attn === k ? null : k);
                  setCollapsed(new Set());
                }}
              >
                <i aria-hidden="true" />
                {t(n)}
              </button>
            ))}
          {attn && (
            <button type="button" className="pl-achip" onClick={() => setAttn(null)}>
              Toon alles
            </button>
          )}
        </div>
      )}

      <div className="pl-bar" role="toolbar" aria-label="Weergave">
        {!phone && (
          <Tabs
            label="Weergave"
            items={[
              { value: "plan", label: "Planning" },
              { value: "data", label: "Gegevens" },
            ]}
            value={view}
            onChange={setView}
          />
        )}
        <label className="pl-search">
          <span className="sr-only">Zoeken</span>
          <input
            type="search"
            value={search}
            placeholder="Zoek asset of uitvoering"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        {hasParts && (
          <select
            className="pl-partfilter"
            aria-label="Onderdelen"
            value={partFilter}
            onChange={(e) => setPartFilter(e.target.value as PartFilter)}
          >
            {PART_FILTERS.map((f) => (
              <option key={f} value={f}>
                {PART_FILTER_LABELS[f]}
              </option>
            ))}
          </select>
        )}
        <div className="pl-chips" role="group" aria-label="Planning filteren op soort werk">
          {WORK_TYPES.map((w) => (
            <button
              key={w}
              type="button"
              className="pl-chip"
              aria-pressed={wts.has(w)}
              onClick={() => {
                const n = new Set(wts);
                if (n.has(w) && n.size > 1) n.delete(w);
                else n.add(w);
                setWts(n);
              }}
            >
              <i className={`pl-tdot wt-${w}`} aria-hidden="true" />
              {WORK_TYPE_LABELS[w]}
            </button>
          ))}
        </div>
        {!showCards && (
          <button
            type="button"
            className="btn small"
            onClick={() => setCollapsed(collapsed.size ? new Set() : new Set(groups.map((g) => g.key ?? "none")))}
          >
            {collapsed.size ? "Alles uitklappen" : "Alles inklappen"}
          </button>
        )}
      </div>

      {view === "plan" && !showCards && (
        <div
          className={`pl-bar pl-zoombar${zoom === "year" ? " yearzoom" : ""}`}
          role="toolbar"
          aria-label="Tijdschaal"
        >
          <Segmented
            label="Inzoomen"
            items={ZOOMS.map((z) => ({ value: z, label: ZOOM_LABELS[z] }))}
            value={zoom}
            onChange={(z) => {
              setZoom(z);
              setAnchor(today);
            }}
          />
          <span className="pl-nav">
            <button
              type="button"
              className="pl-ico"
              aria-label="Eerdere periode"
              onClick={() => setAnchor(shift(zoom, anchor, -1))}
            >
              <IconChevronLeft size={18} aria-hidden="true" />
            </button>
            <span className="pl-range" aria-live="polite">
              {rangeLabel(zoom, cols)}
            </span>
            <button
              type="button"
              className="pl-ico"
              aria-label="Latere periode"
              onClick={() => setAnchor(shift(zoom, anchor, 1))}
            >
              <IconChevronRight size={18} aria-hidden="true" />
            </button>
            <button type="button" className="btn small" onClick={() => setAnchor(today)}>
              Vandaag
            </button>
          </span>
          {zoom !== "year" && (
            <span className="pl-znote">
              Vervanging, herstel en losse regels hebben alleen een jaar: ze staan als balk over dat jaar. Totalen
              tellen alleen regels met een datum.
            </span>
          )}
        </div>
      )}

      {!groups.length ? (
        <EmptyState icon={IconMjop} title="Nog geen planning" text="Dit gebouw heeft nog geen assets of regels." />
      ) : showCards ? (
        <PlanCards
          groups={groups}
          visible={visible}
          filtering={filtering}
          attn={attn}
          canEditObligations={canEditObligations}
          onOpen={openRow}
          onAdd={addOwn}
          onInfo={(a, g) => setInfo({ a, g })}
          onCopy={(a, src) => void copyFrom(a, src)}
          copySource={copySource}
          parentCode={parentCode}
        />
      ) : (
        <div className={`pl-sheet${edit ? " editing" : ""}`}>
          {edit && (
            <div className="pl-editbanner">
              <b>Bewerkmodus</b>
              <span>
                {view === "data"
                  ? "Klik een cel of gebruik de pijltjestoetsen, Enter om te wijzigen, Esc om terug te zetten. Elke wijziging wordt meteen opgeslagen."
                  : "Klik een bedrag van een post om die keer te verschuiven of over te slaan. Keuringen volgen hun vervaldatum."}
              </span>
            </div>
          )}
          <div className="tscroll pl-scroll">
            <table ref={gridRef} className={`pl-grid${zoom !== "year" ? " fine" : ""}`}>
              <thead>
                <tr>
                  <th scope="col" className="pl-first">
                    Asset en uitvoering
                  </th>
                  {view === "plan"
                    ? cols.map((p) => (
                        <th
                          key={p.from}
                          scope="col"
                          className={`r pl-yr${p.now ? " now" : ""}${p.yearStart ? " ys" : ""}`}
                        >
                          {p.label}
                          {p.sub && <small className="pl-hsub">{p.sub}</small>}
                        </th>
                      ))
                    : DATA_COLS.map((c) => (
                        <th key={c.key} scope="col" className={c.num ? "r" : undefined}>
                          {c.label}
                        </th>
                      ))}
                </tr>
              </thead>
              <tbody>{rows}</tbody>
              <tfoot>
                <tr>
                  <th scope="row" className="pl-first">
                    {view === "data" ? "Gemiddeld per jaar" : zoom === "year" ? "Totaal per jaar" : "Totaal met datum"}
                  </th>
                  {view === "plan" ? (
                    sumCells(shownAll, "r num")
                  ) : (
                    <>
                      <td colSpan={ncols - 2} />
                      <td className="r num" data-testid="plan-total">
                        {formatEuro(
                          shownAll.reduce((s, x) => s + (x.r.notApplicable ? 0 : (x.r.perYearCents ?? 0)), 0),
                        )}
                      </td>
                      <td />
                    </>
                  )}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
      <p className="pl-legend">
        Bedragen per keer op prijspeil, exclusief btw; de index staat in de begroting hierboven. Gemiddeld per jaar voor
        dit gebouw: <strong data-testid="plan-avg">{formatEuro(plan.perYearCents)}</strong>
      </p>

      {pop && (
        <Popover anchor={pop.el} label="Acties" onClose={closePop}>
          {pop.kind === "row" && (
            <RowMenu
              x={pop.x}
              sure={Boolean(pop.sure)}
              canEdit={canEditRow(pop.x.r)}
              buildingId={buildingId}
              onOpen={() => {
                setPop(null);
                openRow(pop.x);
              }}
              onDuplicate={() => {
                setPop(null);
                void duplicate(pop.x);
              }}
              onDelete={() => {
                if (!pop.sure) return setPop({ ...pop, sure: true });
                setPop(null);
                void remove(pop.x);
              }}
            />
          )}
          {pop.kind === "occ" && (
            <OccMenu
              x={pop.x}
              year={pop.year}
              occ={pop.occ}
              canEdit={canEditItems}
              buildingId={buildingId}
              onOpen={() => {
                setPop(null);
                openRow(pop.x);
              }}
              onApply={(orig, body) => {
                setPop(null);
                void override(pop.x.r, orig, body);
              }}
            />
          )}
          {pop.kind === "asset" && (
            <>
              <h4 className="pl-poph">
                Toevoegen bij {pop.a.code} {pop.a.name}
              </h4>
              <button type="button" role="menuitem" onClick={() => (setPop(null), addOwn(pop.a))}>
                <IconPlus size={16} aria-hidden="true" />
                Uitvoering toevoegen
              </button>
              {(() => {
                const src = copySource(pop.a, pop.g);
                return (
                  src && (
                    <>
                      <div className="pl-sep" />
                      <button type="button" role="menuitem" onClick={() => (setPop(null), void copyFrom(pop.a, src))}>
                        <IconCopy size={16} aria-hidden="true" />
                        Kopiëren van {src.code} {src.name}
                      </button>
                    </>
                  )
                );
              })()}
            </>
          )}
        </Popover>
      )}

      {drawerX && (
        <RowDrawer
          key={drawerX.r.id}
          x={drawerX}
          canEdit={canEditRow(drawerX.r)}
          canEditItems={canEditItems}
          focusName={Boolean(drawer?.focusName)}
          buildingId={buildingId}
          onClose={() => setDrawer(null)}
          onSave={(key, value) => saveField(drawerX, key, value)}
          onPatch={(patch) => patchRow(drawerX.r, patch)}
          onDetails={() => onDetails(drawerX.r.id)}
        />
      )}
      {showHistory && <BuildingHistory buildingId={buildingId} onClose={() => setShowHistory(false)} />}
      {info && <AssetInfo buildingId={buildingId} a={info.a} groupLabel={info.g.label} onClose={() => setInfo(null)} />}
      {exec && (
        <ExecutionPanel
          key={exec.id ?? "new"}
          assetId={exec.a?.id ?? null}
          buildingId={buildingId}
          assetLabel={exec.a ? `${exec.a.code} · ${exec.a.name}` : "Bij het gebouw"}
          obligationId={exec.id}
          canEdit={canEditObligations}
          showCost
          canReplace={!!exec.a && canEditItems && !exec.a.rows.some((r) => r.type === "vervanging")}
          onReplace={() => {
            const x = exec.id ? byId(exec.id) : null;
            if (!x) return;
            setExec(null);
            void toReplacement(x);
          }}
          onAddReplacement={exec.a ? (o) => run(() => putReplacement(exec.a!, o), "Vervanging toegevoegd") : undefined}
          extra={(() => {
            const x = exec.id ? byId(exec.id) : null;
            return x && <Planned x={x} buildingId={buildingId} />;
          })()}
          onClose={() => setExec(null)}
        />
      )}
    </section>
  );
}

// ---------- cellen ----------

function cellContent(x: Ctx, key: Field | "per" | "status"): ReactNode {
  const r = x.r;
  switch (key) {
    case "type":
      return (
        <span className="pl-typep">
          <i className={`pl-tdot wt-${PLAN_WT[r.type]}`} aria-hidden="true" />
          {PLAN_TYPE_LABELS[r.type]}
        </span>
      );
    case "basis":
      return r.basis ? OBLIGATION_BASIS_LABELS[r.basis] : <span className="pl-empty">—</span>;
    case "start":
      return startText(r);
    case "end":
      return r.endYear ?? <span className="pl-empty">doorlopend</span>;
    case "interval":
      return intervalText(r);
    case "cost":
      if (r.materialsCents)
        return (
          <span title={`${formatEuro(r.costCents ?? 0)} + ${formatEuro(r.materialsCents)} materialen`}>
            {r.costCents == null ? <span className="pl-empty">bedrag invullen</span> : formatEuro(r.costCents)}
            <small className="pl-mat"> + {formatEuro(r.materialsCents)} mat.</small>
          </span>
        );
      return r.costCents == null ? <span className="pl-empty">bedrag invullen</span> : formatEuro(r.costCents);
    case "per":
      return r.perYearCents == null ? <span className="pl-empty">—</span> : formatEuro(r.perYearCents);
    case "status":
      if (!r.status)
        return r.findingCode ? (
          <span className="muted">bevinding {r.findingCode}</span>
        ) : (
          <span className="pl-empty">—</span>
        );
      return (
        <span className={`pl-status st-${r.status}`}>
          <i aria-hidden="true" />
          {OBLIGATION_STATUS_LABELS[r.status]}
          {r.lastPerformedOn && <span className="muted"> · laatst {fmtDay(r.lastPerformedOn)}</span>}
        </span>
      );
  }
}

function CellEditor({
  x,
  field,
  initial,
  options,
  onDone,
}: {
  x: Ctx;
  field: Field;
  initial?: string;
  options: readonly string[] | null;
  /** null = annuleren; anders de waarde en waarheen daarna (rij, kolom). */
  onDone: (value: string | null, move: [number, number] | null) => void;
}) {
  const r = x.r;
  const [value, setValue] = useState(initial ?? rawValue(r, field));
  const done = useRef(false);
  const finish = (v: string | null, move: [number, number] | null) => {
    if (done.current) return;
    done.current = true;
    onDone(v, move);
  };
  const label = `${FIELD_LABELS[field]} ${r.description}`;
  const keys = (e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      finish(value, [1, 0]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      finish(null, null);
    } else if (e.key === "Tab") {
      e.preventDefault();
      finish(value, [0, e.shiftKey ? -1 : 1]);
    }
  };
  if (field === "type" || field === "basis") {
    const opts =
      field === "type"
        ? (options ?? []).map((o) => [o, PLAN_TYPE_LABELS[o as keyof typeof PLAN_TYPE_LABELS]] as const)
        : [
            ...(r.source === "obligation" ? [] : [["", "—"] as const]),
            ...OBLIGATION_BASES.map((b) => [b, OBLIGATION_BASIS_LABELS[b]] as const),
          ];
    return (
      <select
        aria-label={label}
        autoFocus
        value={value}
        onChange={(e) => finish(e.target.value, null)}
        onBlur={() => finish(value, null)}
        onKeyDown={keys}
      >
        {opts.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      aria-label={label}
      autoFocus
      type={field === "start" && r.source === "obligation" ? "date" : "text"}
      inputMode={
        field === "cost" || field === "end" || (field === "start" && r.source === "item") ? "decimal" : undefined
      }
      value={value}
      onFocus={(e) => initial == null && e.currentTarget.select?.()}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => finish(value, null)}
      onKeyDown={keys}
    />
  );
}

// ---------- menu's ----------

function RowMenu({
  x,
  sure,
  canEdit,
  buildingId,
  onOpen,
  onDuplicate,
  onDelete,
}: {
  x: Ctx;
  sure: boolean;
  canEdit: boolean;
  buildingId: string;
  onOpen: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const ob = x.r.source === "obligation";
  return (
    <>
      <button type="button" role="menuitem" onClick={onOpen}>
        <IconPencil size={16} aria-hidden="true" />
        Details openen
      </button>
      {ob && x.a && (
        <Link role="menuitem" className="pl-menulink" to={`/gebouwen/${buildingId}?asset=${x.a.id}`}>
          <IconCheck size={16} aria-hidden="true" />
          {recordLabel(x.r.type)}
        </Link>
      )}
      {ob && canEdit && (
        <button type="button" role="menuitem" onClick={onDuplicate}>
          <IconCopy size={16} aria-hidden="true" />
          Dupliceren
        </button>
      )}
      {canEdit && (
        <>
          <div className="pl-sep" />
          <button type="button" role="menuitem" className="danger" onClick={onDelete}>
            <IconTrash size={16} aria-hidden="true" />
            {sure ? "Klik nogmaals om te verwijderen" : "Verwijderen"}
          </button>
          {x.r.type === "vervanging" && x.a && (
            <p className="pl-pophint">Verwijdert de vervanging; levensduur en prijs bij de asset blijven staan.</p>
          )}
        </>
      )}
    </>
  );
}

function OccMenu({
  x,
  year,
  occ,
  canEdit,
  buildingId,
  onOpen,
  onApply,
}: {
  x: Ctx;
  year: number;
  occ: PlanOccurrence[];
  canEdit: boolean;
  buildingId: string;
  onOpen: () => void;
  onApply: (orig: number, body: Record<string, unknown> | null) => void;
}) {
  const [why, setWhy] = useState(() => occ.find((v) => v.reason)?.reason ?? "");
  const [amount, setAmount] = useState("");
  const r = x.r;
  if (r.source === "obligation" || !canEdit) {
    const ob = r.source === "obligation";
    return (
      <>
        <h4 className="pl-poph">
          {r.description} · {year}
        </h4>
        {ob && (
          <p className="pl-explain">
            Dit schuif je hier niet op. De volgende datum volgt uit de laatste keer dat het is vastgelegd en het
            interval; zonder vastlegging uit de startdatum in de details.
          </p>
        )}
        {ob && x.a && (
          <Link role="menuitem" className="pl-menulink" to={`/gebouwen/${buildingId}?asset=${x.a.id}`}>
            <IconCheck size={16} aria-hidden="true" />
            {recordLabel(r.type)}
          </Link>
        )}
        <button type="button" role="menuitem" onClick={onOpen}>
          <IconPencil size={16} aria-hidden="true" />
          Details openen
        </button>
      </>
    );
  }
  const o = occ[0];
  if (!o) return null;
  const orig = o.orig ?? year;
  const changed = occ.some((v) => v.skipped || v.moved || v.custom);
  const reason = why.trim() || null;
  return (
    <>
      <h4 className="pl-poph">
        {r.description} · {o.moved ? `verschoven van ${orig} naar ${year}` : year}
      </h4>
      <div className="pl-popfield">
        <input
          aria-label="Reden"
          value={why}
          placeholder="Reden (optioneel), bv. combineren met dakwerk"
          maxLength={500}
          onChange={(e) => setWhy(e.target.value)}
        />
      </div>
      <p className="pl-pophint">Je ziet de reden terug bij de post en onder Wijzigingen.</p>
      {changed && (
        <button type="button" role="menuitem" onClick={() => onApply(orig, null)}>
          <IconChevronLeft size={16} aria-hidden="true" />
          Herstellen zoals gepland
        </button>
      )}
      {!o.skipped && (
        <>
          <button
            type="button"
            role="menuitem"
            onClick={() => onApply(orig, { action: "shift", newYear: year - 1, rebaseCycle: false, reason })}
          >
            <IconChevronLeft size={16} aria-hidden="true" />
            Een jaar eerder ({year - 1})
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => onApply(orig, { action: "shift", newYear: year + 1, rebaseCycle: false, reason })}
          >
            <IconChevronRight size={16} aria-hidden="true" />
            Een jaar later ({year + 1})
          </button>
          <button type="button" role="menuitem" onClick={() => onApply(orig, { action: "skip", reason })}>
            <IconClose size={16} aria-hidden="true" />
            Deze keer overslaan
          </button>
          <div className="pl-sep" />
          <form
            className="pl-popfield row"
            onSubmit={(e) => {
              e.preventDefault();
              const cents = Math.round(Number(amount.replace(/\./g, "").replace(",", ".")) * 100);
              if (!amount.trim() || !Number.isFinite(cents)) return;
              onApply(orig, { action: "amount", amountCents: cents, reason });
            }}
          >
            <input
              aria-label={`Bedrag in ${year}`}
              inputMode="decimal"
              value={amount}
              placeholder={`Ander bedrag in ${year}`}
              onChange={(e) => setAmount(e.target.value)}
            />
            <button type="submit" className="btn small">
              Zet
            </button>
          </form>
          <p className="pl-pophint">Alleen deze keer; volgende keren blijven op hun plek.</p>
        </>
      )}
    </>
  );
}

// ---------- paneel ----------

/** Geplande keren en snelkoppeling naar *Keuring vastleggen* of *Onderhoud vastleggen* (onder het formulier van een uitvoering). */
function Planned({ x, buildingId }: { x: Ctx; buildingId: string }) {
  const r = x.r;
  const occ = r.occurrences.filter((o) => !o.skipped).slice(0, 24);
  return (
    <>
      <section className="pl-sect">
        <h3>Gepland</h3>
        {occ.length ? (
          <div className="pl-occlist">
            {occ.map((o, i) => (
              <span key={i} className={`pl-occ wt-${PLAN_WT[r.type]}`}>
                {o.date ? fmtDay(o.date) : o.year}
                {perTime(r) != null && ` · ${kEuro(o.amountCents)}`}
              </span>
            ))}
            {r.occurrences.length > occ.length && <span className="muted small">en verder</span>}
          </div>
        ) : (
          <p className="muted small">Niet binnen de planhorizon.</p>
        )}
      </section>
      {x.a && (
        <p>
          <Link className="btn small" to={`/gebouwen/${buildingId}?asset=${x.a.id}`}>
            {recordLabel(r.type)}
          </Link>
        </p>
      )}
      <HistorySection buildingId={buildingId} entityId={r.id} />
    </>
  );
}

function RowDrawer({
  x,
  canEdit,
  canEditItems,
  focusName,
  buildingId,
  onClose,
  onSave,
  onPatch,
  onDetails,
}: {
  x: Ctx;
  canEdit: boolean;
  canEditItems: boolean;
  focusName: boolean;
  buildingId: string;
  onClose: () => void;
  onSave: (key: PanelField, value: string) => Promise<string | null>;
  onPatch: (patch: Record<string, unknown>) => Promise<string | null>;
  onDetails: () => void;
}) {
  const { r, a } = x;
  const ob = r.source === "obligation";
  const opts = typeOptions(x, canEditItems);
  const [errs, setErrs] = useState<Partial<Record<PanelField, string>>>({});
  const save = async (key: PanelField, value: string, initial: string) => {
    if (value === initial) return;
    const err = await onSave(key, value);
    setErrs((e) => ({ ...e, [key]: err ?? undefined }));
  };
  const text = (key: PanelField, initial: string, props: Record<string, unknown> = {}) => (
    <input
      defaultValue={initial}
      disabled={!canEdit}
      aria-invalid={errs[key] ? true : undefined}
      onBlur={(e) => void save(key, e.target.value, initial)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      {...props}
    />
  );
  const occ = r.occurrences.filter((o) => !o.skipped).slice(0, 24);
  const why = r.occurrences.filter((o) => o.reason);
  return (
    <Drawer
      eyebrow={a ? `${a.code} · ${a.name}` : "Losse regel"}
      title={r.description}
      sub={
        r.status && (
          <span className={`pl-status st-${r.status}`}>
            <i aria-hidden="true" />
            {OBLIGATION_STATUS_LABELS[r.status]}
            {r.start && ` · volgende ${fmtDay(r.start)}`}
          </span>
        )
      }
      onClose={onClose}
      footer={
        <>
          {ob && a && (
            <Link className="btn" to={`/gebouwen/${buildingId}?asset=${a.id}`}>
              {recordLabel(r.type)}
            </Link>
          )}
          {!ob && canEdit && (
            <button type="button" className="btn" onClick={onDetails}>
              Meer velden
            </button>
          )}
          <button type="button" className="btn primary" onClick={onClose}>
            Klaar
          </button>
        </>
      }
    >
      <div className="pl-fgrid">
        <label className="field wide">
          <span className="label">Omschrijving</span>
          {text("name", r.description, focusName ? { "data-autofocus": true, autoFocus: true } : {})}
          {errs.name && <em className="pl-err">{errs.name}</em>}
        </label>
        <label className="field">
          <span className="label">Type</span>
          <select
            defaultValue={r.type}
            disabled={!canEdit || !opts}
            onChange={(e) => void save("type", e.target.value, r.type)}
          >
            {(opts ?? [r.type]).map((o) => (
              <option key={o} value={o}>
                {PLAN_TYPE_LABELS[o]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Eis</span>
          <select
            defaultValue={r.basis ?? ""}
            disabled={!canEdit}
            onChange={(e) => void save("basis", e.target.value, r.basis ?? "")}
          >
            {!ob && <option value="">—</option>}
            {OBLIGATION_BASES.map((b) => (
              <option key={b} value={b}>
                {OBLIGATION_BASIS_LABELS[b]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">{ob ? "Volgende uitvoering" : "Startjaar"}</span>
          {text("start", rawValue(r, "start"), ob ? { type: "date" } : { inputMode: "numeric" })}
          {errs.start && <em className="pl-err">{errs.start}</em>}
        </label>
        <label className="field">
          <span className="label">Stopjaar</span>
          {text("end", rawValue(r, "end"), { inputMode: "numeric", placeholder: "doorlopend" })}
          {errs.end && <em className="pl-err">{errs.end}</em>}
        </label>
        <label className="field">
          <span className="label">Interval</span>
          {text("interval", rawValue(r, "interval"))}
          <em className="pl-hint">
            {errs.interval ?? (ob ? "1 wk, 2 wk, 6 mnd, 5 jr of eenmalig" : "In hele jaren, bijvoorbeeld 5 jr")}
          </em>
        </label>
        <label className="field">
          <span className="label">Per keer (excl. btw)</span>
          {text("cost", rawValue(r, "cost"), { inputMode: "decimal", placeholder: "bedrag" })}
          <em className="pl-hint">
            {errs.cost ??
              (r.perYearCents != null ? `Gemiddeld ${formatEuro(r.perYearCents)} per jaar` : "Eenmalig of onbekend")}
          </em>
        </label>
        {!ob && r.quantity != null && (
          <label className="field" key={`qty-${r.quantity}-${r.unit}`}>
            <span className="label">
              Hoeveelheid{r.unit ? ` (${LIBRARY_UNIT_LABELS[r.unit as LibraryUnit] ?? r.unit})` : ""}
            </span>
            {text("qty", String(r.quantity).replace(".", ","), { inputMode: "decimal" })}
            <em className="pl-hint" data-testid="plan-qty-hint">
              {errs.qty ??
                (!a || r.type !== "vervanging" || a.quantity == null || !(a.quantity > 0)
                  ? r.unit === "post"
                    ? "Post: één bedrag"
                    : "Per keer = hoeveelheid × prijs per eenheid"
                  : r.unit === "post"
                    ? "Post: één bedrag, los van het aantal van de asset"
                    : ownQuantity(x)
                      ? `Eigen aantal; de asset heeft ${quantityText(a.quantity, a.unit)}`
                      : "Van de asset; past mee aan als de asset verandert")}
            </em>
            {canEdit && ownQuantity(x) && a?.quantity != null && (
              <button
                type="button"
                className="linkbtn inline"
                onClick={() => void onPatch({ quantity: a.quantity, unit: a.unit ?? r.unit })}
              >
                Aantal van de asset gebruiken
              </button>
            )}
          </label>
        )}
      </div>
      <section className="pl-sect">
        <h3>Gepland</h3>
        {occ.length ? (
          <div className="pl-occlist">
            {occ.map((o, i) => (
              <span
                key={i}
                className={`pl-occ wt-${PLAN_WT[r.type]}${o.moved ? " moved" : ""}`}
                title={o.reason ? `Reden: ${o.reason}` : undefined}
              >
                {o.date ? fmtDay(o.date) : o.year}
                {perTime(r) != null && ` · ${kEuro(o.amountCents)}`}
              </span>
            ))}
            {r.occurrences.length > occ.length && <span className="muted small">en verder</span>}
          </div>
        ) : (
          <p className="muted small">Niet binnen de planhorizon.</p>
        )}
        {why.length > 0 && (
          <ul className="pl-whylist" aria-label="Redenen">
            {why.map((o) => (
              <li key={o.orig ?? o.year}>
                <strong>
                  {o.skipped ? `${o.year} overgeslagen` : o.moved ? `${o.orig} → ${o.year}` : `${o.year}`}
                </strong>
                <span>{o.reason}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      {ob && (
        <section className="pl-sect">
          <h3>Laatste keer</h3>
          <p className="small">
            {r.lastPerformedOn
              ? `${fmtDay(r.lastPerformedOn)}${r.lastResult ? ` · ${kindText(r.type).results[r.lastResult]}` : ""}`
              : "Nog niet vastgelegd."}
          </p>
        </section>
      )}
      {r.findingCode && (
        <section className="pl-sect">
          <h3>Bron</h3>
          <p className="small muted">
            Raming bij bevinding {r.findingCode}. Afhandelen van de bevinding zet deze post op uitgevoerd.
          </p>
        </section>
      )}
      <HistorySection buildingId={buildingId} entityId={r.id} />
    </Drawer>
  );
}

// ---------- telefoon ----------

function PlanCards({
  groups,
  visible,
  filtering,
  attn,
  canEditObligations,
  onOpen,
  onAdd,
  onInfo,
  onCopy,
  copySource,
  parentCode,
}: {
  groups: PlanGroupDto[];
  visible: (x: Ctx) => boolean;
  filtering: boolean;
  attn: Attn | null;
  canEditObligations: boolean;
  onOpen: (x: Ctx) => void;
  onAdd: (a: PlanAssetDto) => void;
  onInfo: (a: PlanAssetDto, g: PlanGroupDto) => void;
  onCopy: (a: PlanAssetDto, src: PlanAssetDto) => void;
  copySource: (a: PlanAssetDto, g: PlanGroupDto) => PlanAssetDto | null;
  parentCode: (id: string) => string;
}) {
  const row = (x: Ctx) => {
    const r = x.r;
    const next = r.occurrences.find((o) => !o.skipped);
    const when =
      r.source === "obligation" ? (
        r.status === "overdue" ? (
          <span className="pl-overdue">verlopen sinds {fmtDay(r.start)}</span>
        ) : next?.date ? (
          `volgende ${fmtDay(next.date)}`
        ) : (
          "geen datum"
        )
      ) : next ? (
        `in ${next.year}`
      ) : (
        "na de planhorizon"
      );
    return (
      <button key={r.id} type="button" className="pl-crow" onClick={() => onOpen(x)}>
        <i className={`pl-tdot wt-${PLAN_WT[r.type]}`} aria-hidden="true" />
        <span className="pl-ct">
          <b>{r.description}</b>
          <small>
            {PLAN_TYPE_LABELS[r.type]} · {intervalText(r)} · {when}
          </small>
        </span>
        <span className="pl-camt">
          {perTime(r) == null ? <span className="pl-empty">—</span> : formatEuro(perTime(r)!)}
          <small>{r.perYearCents != null ? `${formatEuro(r.perYearCents)} /jr` : "per keer"}</small>
        </span>
      </button>
    );
  };
  return (
    <div className="pl-cards">
      {groups.map((g) => {
        const assets = g.assets.filter((a) =>
          attn === "empty"
            ? !a.rows.length && !a.parentAssetId
            : !filtering || a.rows.some((r) => visible({ r, a, g })),
        );
        const loose = attn === "empty" ? [] : g.loose.map((r) => ({ r, a: null, g })).filter(visible);
        if (!assets.length && !loose.length) return null;
        return (
          <section key={g.key ?? "none"} className="pl-cgroup" aria-label={g.label}>
            <header>
              <span>{g.label}</span>
              <small>{formatEuro(g.perYearCents)} per jaar</small>
            </header>
            {assets.map((a) => {
              const src = copySource(a, g);
              return (
                <div key={a.id} className={`pl-card${a.parentAssetId ? " pl-part" : ""}`}>
                  <header>
                    <span className="pl-acode">{a.code}</span>
                    <span className="grow">{a.name}</span>
                    <button
                      type="button"
                      className="pl-ico pl-ainfo"
                      aria-label={`Informatie over ${a.code} ${a.name}`}
                      onClick={() => onInfo(a, g)}
                    >
                      <IconInfo size={16} aria-hidden="true" />
                    </button>
                  </header>
                  {a.rows
                    .map((r) => ({ r, a, g }))
                    .filter(visible)
                    .map(row)}
                  {a.rows.length > 0 && canEditObligations && (
                    <div className="pl-cpad">
                      <button type="button" className="pl-addbtn" onClick={() => onAdd(a)}>
                        <IconPlus size={14} aria-hidden="true" />
                        Uitvoering bij {a.code}
                      </button>
                    </div>
                  )}
                  {!a.rows.length && a.parentAssetId && (
                    <div className="pl-sact pl-cpad">
                      <span className="muted small">Valt onder {parentCode(a.parentAssetId)}.</span>
                      {canEditObligations && (
                        <button type="button" className="btn small" onClick={() => onAdd(a)}>
                          <IconPlus size={14} aria-hidden="true" />
                          Eigen post
                        </button>
                      )}
                    </div>
                  )}
                  {!a.rows.length && !a.parentAssetId && canEditObligations && (
                    <div className="pl-sact pl-cpad">
                      <span className="muted small">Nog niets gepland.</span>
                      <button type="button" className="btn small primary" onClick={() => onAdd(a)}>
                        <IconPlus size={14} aria-hidden="true" />
                        Uitvoering toevoegen
                      </button>
                      {src && (
                        <button type="button" className="btn small" onClick={() => onCopy(a, src)}>
                          Kopiëren van {src.code}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {loose.length > 0 && (
              <div className="pl-card">
                <header>Losse regels</header>
                {loose.map(row)}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
